import { ALL_COUNTRIES, COUNTRY_NAMES } from "@/utils/countries";
import { ENERGY_TECHNOLOGIES, TECHNOLOGIES } from "@/types/technology";
export { ALL_COUNTRIES, COUNTRY_NAMES };

export const MAIN_SIGNUP_FLOW_COUNTRIES = ["EE", "LV", "LT", "SE", "ES", "PL"];

// EU member states (27 countries as of 2024)
export const EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

export const SUPPORTED_COUNTRIES_WITH_LABELS = [
  { value: "AT", label: "Austria" },
  { value: "BE", label: "Belgium" },
  { value: "BG", label: "Bulgaria" },
  { value: "HR", label: "Croatia" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czechia" },
  { value: "DK", label: "Denmark" },
  { value: "EE", label: "Estonia" },
  { value: "ES", label: "Spain" },
  { value: "FI", label: "Finland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "GR", label: "Greece" },
  { value: "HU", label: "Hungary" },
  { value: "IS", label: "Iceland" },
  { value: "IE", label: "Ireland" },
  { value: "IT", label: "Italy" },
  { value: "LV", label: "Latvia" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "MT", label: "Malta" },
  { value: "NL", label: "Netherlands" },
  { value: "NO", label: "Norway" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "RO", label: "Romania" },
  { value: "SK", label: "Slovakia" },
  { value: "SI", label: "Slovenia" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  { value: "GB", label: "United Kingdom" },
];

// "Any AIB" cancellation-flow marker (DEV-576). A sentinel the cancel editor's registry dropdown
// holds while editing; it is never a real registry (kept out of SUPPORTED_REGISTRIES_WITH_LABELS) and
// materializes to "NO" on submit, carrying is_any_aib=true. Distinct from the ANY_AIB_REGISTRY
// CancellationMode enum value (a different concept).
export const ANY_AIB_REGISTRY_SENTINEL = "ANY_AIB";

export const SUPPORTED_REGISTRIES_WITH_LABELS = [
  { value: "AT", operator: "E-Control" },
  { value: "BE-BRU", operator: "BRUGEL" },
  { value: "BE-FED", operator: "CREG" },
  { value: "BE-FLA", operator: "VREG" },
  { value: "BE-WAL", operator: "e-CWaPE" },
  { value: "HR", operator: "HROTE" },
  { value: "CY", operator: "TSO-Cy" },
  { value: "CZ", operator: "OTE" },
  { value: "DK", operator: "Energinet" },
  { value: "EE", operator: "Elering" },
  { value: "FI", operator: "Finextra" },
  { value: "FR", operator: "EEX" },
  { value: "DE", operator: "HKNR" },
  { value: "GR", operator: "DAPEEP" },
  { value: "HU", operator: "MEKH" },
  { value: "IS", operator: "Landsnet" },
  { value: "IE", operator: "SEMO" },
  { value: "IT", operator: "GSE" },
  { value: "LV", operator: "AST" },
  { value: "LT", operator: "Litgrid" },
  { value: "LU", operator: "ILR" },
  { value: "NL", operator: "VertiCer" },
  { value: "NO", operator: "Statnett" },
  { value: "PT", operator: "EEGO" },
  { value: "RS", operator: "EMS" },
  { value: "SK", operator: "OKTE" },
  { value: "SI", operator: "Borzen" },
  { value: "PL", operator: "TGE" },
  { value: "ES", operator: "CNMC" },
  { value: "SE", operator: "Energy Agency" },
  { value: "CH", operator: "Pronovo" },
  { value: "GB", operator: "Ofgem" },
];

export const LANGUAGE_TO_COUNTRY = {
  et: "EE",
  lv: "LV",
  lt: "LT",
  sv: "SE",
  fi: "FI",
  es: "ES",
} as const;

export const ADMIN_IDS = {
  // TODO add proper access control
  FULL_ACCESS: [16, 24, 513, 548], // CEO and CFO admin IDs with full access to commission data
};

export const PORTFOLIO_TYPES = [
  { portfolio_type: "UNALLOCATED", name: "Unallocated" },
  { portfolio_type: "FORWARD", name: "Forward" },
  { portfolio_type: "HOLD", name: "Hold" },
  { portfolio_type: "SPOT_MARKET_PRICE", name: "Spot Market Price" },
  { portfolio_type: "SPOT_STX", name: "Spot STX" },
  { portfolio_type: "SPOT_MIN_PRICE", name: "Spot Min Price" },
  { portfolio_type: "OWN_USE", name: "Own use" },
];

export const SELLABLE_PORTFOLIO_TYPES = [
  "SPOT_MARKET_PRICE",
  "SPOT_STX",
  "SPOT_MIN_PRICE",
];

export const INFORMATIONAL_PORTFOLIO_TYPES = new Set([
  "HOLD",
  "PPA",
  "VIRTUAL",
  "UNALLOCATED",
  "OWN_USE",
]);

// List of technology types with their prefix codes (projected technologies, registry order)
export const TECHNOLOGY_TYPES = ENERGY_TECHNOLOGIES.map((key) => ({
  tech_code_prefix: TECHNOLOGIES[key].prefix,
  name: TECHNOLOGIES[key].name,
}));

// Countries in the standard AIB Guarantees of Origin certification scheme.
// Keep in sync with `_AIB_MEMBER_CODE_TO_ISO2_COUNTRY` in app/utils/eecs_utils.py.
// Mirrored by AIB_GC_COUNTRIES in app/constants.py. tests/forwards2/test_aib_country_parity.py parses
// the two array literals below out of this file, and holds the backend's standard list to the literal
// that client/tests/constants.spec.ts pins the filtered one to, so drift on either side fails a test.
export const AIB_GC_COUNTRIES = [
  "AT",
  "BE",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LT",
  "LU",
  "LV",
  "NL",
  "NO",
  "PT",
  "RS",
  "SE",
  "SI",
  "SK",
];

// AIB GC member countries whose Guarantees of Origin trade at a premium.
// They are excluded from the "Standard AIB GC" quick filter (see below), but
// are still valid AIB GC members and remain in AIB_GC_COUNTRIES above.
export const PREMIUM_AIB_GC_COUNTRIES = ["AT", "CH", "DE", "NL"];

// AIB GC members that sell as their own product rather than as standard AIB
// GC. Belgium issues as BE-FLA / BE-BRU, which the Transactions view's region
// filter never matched, so leaving it out keeps the forwards picker's filter
// the same product as the spot sale's.
export const NON_STANDARD_AIB_GC_COUNTRIES = ["BE"];

// AIB GC countries at standard (non-premium) pricing, used by the
// "Standard AIB GC" quick filters on the Transactions view and the forwards
// picker. Excludes the premium and non-standard countries above, and IS
// (Iceland) which is an AIB member but is not part of the GC scheme.
export const STANDARD_AIB_GC_COUNTRIES = AIB_GC_COUNTRIES.filter(
  (country) =>
    !PREMIUM_AIB_GC_COUNTRIES.includes(country) &&
    !NON_STANDARD_AIB_GC_COUNTRIES.includes(country) &&
    country !== "IS"
);

// Make sure to update these URLs in the Backend constants.py as well !!!!!!
export const FORWARD_OUTLOOK_URL =
  "https://storage.googleapis.com/soldera-marketing-public/Outlooks/Soldera_2026_October_Outlook.pdf";

export const SHORT_FORWARD_OUTLOOK_URL =
  "https://storage.googleapis.com/soldera-marketing-public/Outlooks/Soldera_2026_October_Outlook_Short.pdf";

export const FORWARD_OUTLOOK_LAST_UPDATED = "October 2026";

// Market Insights Data URLs
export const EU_GENERATION_DATA_URL =
  "https://static.soldera.org/gendata/eu_gen_processed.json";

export const RESERVOIR_LEVELS_URLS = {
  NORWAY_TOTAL:
    "https://static.soldera.org/reservoir/static/future_predictions_NO.csv",
  NO1: "https://static.soldera.org/reservoir/static/future_predictions_NO1.csv",
  NO2: "https://static.soldera.org/reservoir/static/future_predictions_NO2.csv",
  NO3: "https://static.soldera.org/reservoir/static/future_predictions_NO3.csv",
  NO4: "https://static.soldera.org/reservoir/static/future_predictions_NO4.csv",
  NO5: "https://static.soldera.org/reservoir/static/future_predictions_NO5.csv",
};

/** Mirrors SOLDERA_SUPPORT_EMAIL in app/constants.py. */
export const SOLDERA_SUPPORT_EMAIL = "support@soldera.org";

// Company Feature Flags
// Feature flags are defined in the CompanyFeatureFlags model in app/models/account_models.py
// When adding a new feature flag:
// 1. Add the field to CompanyFeatureFlags model in app/models/account_models.py
// 2. Add the human-readable label to this mapping

export const FEATURE_FLAGS = {
  invoice_device_breakdown: "Invoice Device Breakdown",
  hold_allocation: "Hold Allocation",
  ppa_allocation: "PPA Allocation",
  allocation_ready: "Ready for Automatic Allocation",
  trusted_customer:
    "Trusted Customer (invites, statement uploads, unlimited AI)",
  skip_first_year_referral: "Skip First Year Referral",
  big_company_view_enabled: "Big Company View Enabled",
  buy_EACs_enabled: "Buy EACs Enabled",
  corporate_view_enabled: "Corporate View Enabled",
  corporate_virtual_accounts_enabled: "Corporate Hosted Accounts Enabled",
  trader_view_enabled: "Trader View Enabled",
  ecolabels_enabled: "Ecolabels Enabled",
  ekoenergy_authorized_seller: "EKOenergy Authorized Seller",
  manual_production_estimates: "Manual Production Estimates",
  income_estimate_enabled: "Income Estimate",
  ledger_reads:
    "Ledger Reads (DEV-579: invoicing reads from the obligation ledger)",
  international_self_signup:
    "International Self-Signup (awaiting manual onboarding)",
  month_aligned_payout_invoice: "Month-aligned payout invoice (EE)",
} as const;

// Account Feature Flags
// Feature flags are defined in the AccountFeatureFlags model in app/models/account_models.py
// When adding a new feature flag:
// 1. Add the field to AccountFeatureFlags model in app/models/account_models.py
// 2. Add the human-readable label to this mapping
export const ACCOUNT_FEATURE_FLAGS = {
  hknr_bulk_issuance_report_enabled: "HKNR Bulk Issuance Report",
  ekoenergy_admin_enabled: "EKOenergy Admin Access",
  detailed_reservoir_levels_enabled: "Detailed Reservoir Levels",
  compliance_groups_demo_enabled: "Compliance Groups Demo",
  realtime_soldera_inventory: "Realtime Soldera Inventory",
} as const;

// Month names for charts and date displays
export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
